import ReactDOM from 'react-dom';
import {
  FC,
  ReactNode,
  useRef,
  useContext,
  CSSProperties,
  memo,
} from 'react';
// 组件
import { DownOutlined, RollbackOutlined } from '@ant-design/icons';
import DraftsFormModal from './draftsFormModal';
import { ProSpace, ProFloatButton, ProRadio, ProDropdown, ProButton, } from '../../antd';
import Steps, { StepsProps } from '../../Steps';
import OperationButton from '../../OperationButton';
// 方法
import classnames from 'classnames';
import DetailPageContext, { IDetailPageContext } from '../context';
import { useLayoutContext } from '../../PageLayout/context/LayoutContext';
import { getHTMLElement } from '../../utils/utils';
import { getTitle } from '../util';
// 常量
import { ButtonType, ButtonProps } from 'antd/lib/button';
import {
  TABLE_OK_BUTTON_ID,
  TABLE_CANCEL_BUTTON_ID,
  TABLE_DETAIL_BUTTON_ID,
  TABLE_DRAFTS_BUTTON_ID,
} from '../../constants/constant';
import { FormModalActionType } from '../../FormModal/interface';
import { DetailButtonType, sizeenum } from '../interface';
import { MenuProps } from 'antd';

export type textAlignType = 'left' | 'right' | 'center';
export type sizeType = 'full' | 'large' | 'middle' | 'small' | false;
export interface detailDraftsProps {
  createFetchApi?: (params: any) => Promise<any>;
  updateFetchApi?: (params: any) => Promise<any>;
  onUpdateCount?: () => void;
  onSuccess?: (res: any) => void;
  onFail?: (res: any) => void;
  text?: string;
  buttonType?: ButtonType; // 草稿箱按钮type
  buttonProps?: ButtonProps; // 草稿箱按钮其他状态
}
export interface IBaseProps {
  /** 返回文字 */
  backText?: ReactNode;
  /** 详情页标题 */
  title?: ReactNode;
  /** 详情头部右侧操作按钮 */
  footer?: ReactNode[] | false;
  /** 确定按钮type */
  okType?: ButtonType;
  /** 取消按钮type */
  cancelType?: ButtonType;
  /** 确定按钮文字 */
  okText?: ReactNode;
  /** 新增按钮文字 */
  addButtonText?: ReactNode;
  /** 编辑按钮文字 */
  editButtonText?: ReactNode;
  /** 取消编辑按钮文字 */
  cancelEditButtonText?: ReactNode;
  /** 是否显示取消编辑按钮 */
  showCancelEditButton?: boolean;
  /** 确定按钮是否处于loading状态 */
  confirmLoading?: boolean;
  /** 确定按钮其他状态 */
  okButtonProps?: ButtonProps;
  /** 取消按钮文字 */
  cancelText?: ReactNode;
  /** 页面对齐方式 */
  textAlign?: textAlignType;
  /** 页面宽度 优先级较高 */
  width?: string | number;
  /** 页面宽度 */
  size?: sizeType;
  /** 页面挂载节点或者id */
  getContainer?: HTMLElement | string;
  /** 关联列表的id */
  tableId?: HTMLElement | string;
  /** 编辑按钮文字 */
  detailText?: ReactNode;
  /** 编辑按钮其他状态 */
  detailButtonProps?: ButtonProps;
  /** 编辑按钮type */
  detailType?: ButtonType;
  /** 是否隐藏确定或者编辑按钮 */
  hideOkButton?: boolean;
  /** 编辑按钮权限 */
  editButtonAuthorityCode?: string;
  /** 编辑按钮权限 */
  isEditAuth?: boolean;
  /** 内容样式 */
  contentStyle?: CSSProperties;
  /** 测试ID */
  testId?: string;
  /** 关闭时还原清空表单项 */
  destroyOnClose?: boolean;
  /** 最外层容器类名 */
  wrapperClassName?: string;
  /** 头部容器类名 */
  headClassName?: string;
  /** 内容容器类名 */
  bodyClassName?: string;
  /** 草稿箱数据 */
  draftsData?: any;
  /** 草稿箱配置 */
  drafts?: detailDraftsProps | false;
  /** 详情内容是否要占满整个页面 */
  isFullPage?: boolean;
  /** 打开详情页时需要隐藏的className */
  otherHideClassName?: string;
  /** 最小宽度 */
  minWidth?: string | number;
  /** 是否隐藏返回按钮 */
  hideBack?: boolean;
  /** 头部进度条 */
  headerProps?: StepsProps;
  /** 自定义详情页底部 */
  footerNode?: ReactNode;
  /** 底部类名 */
  footerClassName?: string;
  /** 底部样式 */
  footerStyle?: CSSProperties;
  /** 其他按钮 */
  otherButtons?: DetailButtonType[];
  /** 子节点 */
  children?: ReactNode;
  /** 详情页显示隐藏切换回调 */
  onVisibleChange?: (visible: boolean) => void;
  /** 保存草稿箱 */
  onDrafts?: (values: any) => void;
  /** 点击返回或者关闭回调 */
  onBack?: () => void;
  /** 点击编辑按钮回调 */
  onDetail?: () => void;
  /** 点击其他按钮 */
  onClickOtherButton?: (key: string) => void;
  /** 点击取消编辑回调 */
  onCancelEdit?: () => void;
}
export interface BaseDetailPageProps extends IBaseProps {
  onOk?: () => void; // 点击确定回调
}

const BaseDetailPage: FC<BaseDetailPageProps> = memo(props => {
  const {
    backText,
    title,
    footer,
    okType,
    okText,
    confirmLoading,
    okButtonProps,
    cancelText,
    detailText,
    detailType,
    detailButtonProps,
    textAlign,
    editButtonAuthorityCode,
    isEditAuth,
    size,
    width,
    getContainer,
    hideOkButton,
    contentStyle,
    otherButtons,
    headClassName,
    bodyClassName,
    drafts,
    draftsData,
    isFullPage,
    minWidth,
    hideBack,
    headerProps,
    footerNode,
    footerClassName,
    footerStyle,
    addButtonText,
    editButtonText,
    cancelEditButtonText,
    showCancelEditButton,
    onBack,
    onOk,
    onDetail,
    onDrafts,
    onClickOtherButton,
    onCancelEdit,
    children,
  } = props;
  const renderRef = useRef<HTMLElement>();
  const detailBodyRef = useRef<HTMLDivElement>(null);
  const formModalRef = useRef<FormModalActionType>();
  const { dispatch } = useLayoutContext();
  const { pageType, dispatchDetailPage } = useContext<IDetailPageContext>(DetailPageContext);
  const currentTitle = title || getTitle(pageType);
  // 返回
  const handleBack = () => {
    dispatchDetailPage('change', { visible: false });
    onBack?.();
  };
  // 确定
  const handleOk = () => {
    onOk?.();
  };
  // 编辑
  const handleEdit = () => {
    dispatchDetailPage('change', { pageType: 'edit' });
    onDetail?.();
  };
  // 取消编辑
  const handleCancelEdit = () => {
    dispatchDetailPage('change', { pageType: 'detail' });
    onCancelEdit?.();
  };
  // 草稿箱
  const handleDrafte = () => {
    formModalRef.current?.show();
  };
  const getFooter = () => {
    if (footer === false) return;
    if (Array.isArray(footer)) {
      const firstFooter = footer.slice(0, 1);
      const restFooter = footer.slice(1);
      return (
        <ProSpace align="center" size="middle">
          {firstFooter}
          {pageType && pageType === 'add' && drafts && (
            <>
              <OperationButton.Drafts
                id={TABLE_DRAFTS_BUTTON_ID}
                key="drafts1"
                type={drafts.buttonType}
                {...drafts.buttonProps}
                onClick={handleDrafte}
                text={drafts.text}
              />
              <DraftsFormModal
                draftsData={draftsData}
                actionRef={formModalRef}
                onOk={onDrafts}
              />
            </>
          )}
          {restFooter}
        </ProSpace>
      );
    }
    return (
      <ProSpace align="center" size="middle">
        {hideOkButton || !pageType ? null : pageType === 'detail' ? (
          <>
            {
              (
                (isEditAuth === undefined || isEditAuth === true) ||
                (!editButtonAuthorityCode || dispatch('isAuthority', editButtonAuthorityCode)))
               ? (
                <OperationButton.Edit
                  id={TABLE_OK_BUTTON_ID}
                  key="2"
                  type={detailType}
                  {...detailButtonProps}
                  onClick={handleEdit}
                  text={detailText}
                />
              ) : null
            }
          </>
        ) : (
          <>
            <OperationButton.Submit
              id={TABLE_DETAIL_BUTTON_ID}
              key="4"
              type={okType}
              loading={confirmLoading}
              {...okButtonProps}
              onClick={handleOk}
              text={(pageType === 'add' ? addButtonText : editButtonText) || okText}
            />
            {
              showCancelEditButton && pageType === 'edit' && (
                <OperationButton.Cancel
                  key='5'
                  onClick={handleCancelEdit}
                  text={cancelEditButtonText}
                />
              )
            }
          </>
        )}
        {pageType && pageType === 'add' && drafts && (
          <>
            <OperationButton.Drafts
              id={TABLE_DRAFTS_BUTTON_ID}
              key="drafts2"
              type={drafts.buttonType}
              {...drafts.buttonProps}
              onClick={handleDrafte}
              text={drafts.text}
            />
            <DraftsFormModal
              draftsData={draftsData}
              actionRef={formModalRef}
              onOk={onDrafts}
            />
          </>
        )}
        {
          Array.isArray(otherButtons) && !!otherButtons.length && (
            <>
              {
                otherButtons.map(item => {
                  if (Array.isArray(item)) {
                    if (!item.length) return null;
                    return (
                      <ProRadio.Group
                        optionType="button"
                        buttonStyle="outline"
                        disabled={pageType !== 'detail'}
                      >
                        {
                          item.map(v => {
                            if (v.render) {
                              const renderClasses = classnames('detail-render-button', {
                                disabled: v.disabled || pageType !== 'detail',
                              });
                              return (
                                <div
                                  className={renderClasses}
                                  key={v.key}
                                  onClick={() => {
                                    !v.disabled && onClickOtherButton?.(v.key)
                                  }}
                                >
                                  {v.icon}
                                  <span className={v.icon ? 'button-icon-left' : undefined}>{v.render}</span>
                                </div>
                              );
                            }
                            return (
                              <ProRadio.Button
                                key={v.key}
                                value={v.key}
                                disabled={v.disabled}
                                onClick={() => onClickOtherButton?.(v.key)}
                              >
                                <span>{v.icon}</span>
                                <span className='button-icon-left'>{v.label}</span>
                              </ProRadio.Button>
                            );
                          })
                        }
                      </ProRadio.Group>
                    );
                  }
                  if (item.label && Array.isArray(item.buttonList) && item.buttonList.length) {
                    const items: MenuProps['items'] = [];
                    item.buttonList.forEach(item => {
                      items.push({
                        key: item.key,
                        icon: item.icon,
                        label: item.render || item.label,
                      });
                    });
                    return (
                      <ProDropdown
                        key={item.key}
                        trigger={['click']}
                        disabled={pageType !== 'detail'}
                        menu={{
                          items,
                          onClick: v => onClickOtherButton?.(v.key),
                        }}
                      >
                        <ProButton icon={item.icon}>
                          {item.label}
                          <DownOutlined />
                        </ProButton>
                      </ProDropdown>
                    );
                  }
                  return null;
                })
              }
            </>
          )
        }
        <OperationButton.Cancel
          id={TABLE_CANCEL_BUTTON_ID}
          key="6"
          type="default"
          onClick={handleBack}
          text={cancelText}
        />
      </ProSpace>
    );
  };
  const classes = classnames('tl-detail-body', bodyClassName, {
    [`content-${textAlign}`]: textAlign,
    // 'content-border': headerProps,
    // 'full-page': isFullPage,
  });
  let contentStype: CSSProperties = { ...contentStyle };
  if (isFullPage) contentStype.height = '100%';
  if (size) {
    contentStype.width = sizeenum[size];
  }
  if (width) {
    if (typeof width === 'number') {
      contentStype.width = `${width}px`;
    }
    if (typeof width === 'string') {
      if (width.endsWith('%')) {
        contentStype.width = `${window.parseInt(width, 10)}%`;
      } else {
        contentStype.width = `${window.parseInt(width, 10)}px`;
      }
    }
  }
  if (minWidth) {
    contentStype.minWidth = `${minWidth}px`;
  }
  const headClasses = classnames('tl-detail-head', headClassName, {
    'head-steps': headerProps,
  });
  const footerClasses = classnames('tl-detail-footer', footerClassName);
  const renderHTML = (
    <div className="tl-detail">
      <div className={headClasses}>
        <div className="tl-detail-head-top">
          <div className="tl-detail-head-top-title">
            {!hideBack && (
              <div
                className="tl-detail-head-top-title-back"
                onClick={handleBack}
              >
                <RollbackOutlined className='p-r-s' />
                <span>{backText}</span>
              </div>
            )}
            <div className="tl-detail-head-top-title-name">{currentTitle}</div>
          </div>
          <div className="tl-detail-head-top-operation">{getFooter()}</div>
        </div>
        {!!headerProps && (
          <Steps className="tl-detail-head-steps" {...headerProps} />
        )}
      </div>
      <div className={classes} ref={detailBodyRef}>
        <div className="tl-detail-body-content" style={contentStype}>
          {children}
        </div>
        <ProFloatButton.BackTop
          visibilityHeight={400}
          target={() => detailBodyRef.current || document.body}
        />
      </div>
      {
        !!footerNode && (
          <div className={footerClasses} style={footerStyle}>
            {footerNode}
          </div>
        )
      }
    </div>
  );
  if (getContainer) {
    if (!renderRef.current) {
      const el = getHTMLElement(getContainer);
      if (el) {
        renderRef.current = el;
        return ReactDOM.createPortal(renderHTML, el);
      }
      return null;
    }
    return ReactDOM.createPortal(renderHTML, renderRef.current);
  }
  return renderHTML;
});

BaseDetailPage.defaultProps = {
  backText: '返回',
  okType: 'primary',
  cancelType: 'default',
  okText: '确定',
  addButtonText: '确定',
  editButtonText: '保存',
  cancelEditButtonText: '取消编辑',
  confirmLoading: false,
  cancelText: '关闭',
  detailType: 'primary',
  detailText: '编辑',
  hideOkButton: false,
  hideBack: false,
  showCancelEditButton: false,
};

export default BaseDetailPage;
